// src/SessionsPage.js
import React, { useState, useMemo, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Swords, Target, Trophy, Crown, ChevronRight,
  Search, Clock, Shield, Loader2,
  CalendarDays, Zap, BarChart2, TrendingUp, TrendingDown, Bot, FileText,
} from 'lucide-react';
import { auth, firestore } from './firebase';
import {
  collection, query, orderBy, limit, getDocs,
} from 'firebase/firestore';
import { useChallengeStorage } from './hooks/useChallengeStorage';
import { Avatar } from './battleUtils';
import Sidebar from './Sidebar';
import './SessionsPage.css';

// ─── Shared helpers ──────────────────────────────────────────────────────────
const getTierColor = (score) => {
  if (score == null) return '#9CA3AF';
  if (score >= 90) return '#10B981';
  if (score >= 70) return '#3B82F6';
  if (score >= 50) return '#F59E0B';
  return '#EF4444';
};

const formatSessionDate = (rawDate) => {
  const date = rawDate
    ? new Date(rawDate.seconds ? rawDate.seconds * 1000 : rawDate)
    : new Date();
  return {
    dateStr: date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
    timeStr: date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }),
  };
};

// ─── Score chip ───────────────────────────────────────────────────────────────
const ScoreChip = ({ score, size = 'md' }) => {
  const color = getTierColor(score);
  return (
    <span
      className={`sp-score-chip sp-score-chip--${size}`}
      style={{ background: `${color}14`, color, border: `1px solid ${color}35` }}
    >
      {score}
    </span>
  );
};

// ─── Stat bar ────────────────────────────────────────────────────────────────
const StatBar = ({ label, value, max = 100 }) => {
  const pct   = Math.min(100, Math.round((value / max) * 100));
  const color = pct >= 75 ? '#10B981' : pct >= 50 ? '#F59E0B' : '#EF4444';
  return (
    <div className="sp-stat-bar">
      <div className="sp-stat-bar-top">
        <span className="sp-stat-bar-label">{label}</span>
        <span className="sp-stat-bar-val" style={{ color }}>{value}</span>
      </div>
      <div className="sp-stat-bar-track">
        <div className="sp-stat-bar-fill" style={{ width: `${pct}%`, background: color }} />
      </div>
    </div>
  );
};

// ─── Fact row ────────────────────────────────────────────────────────────────
const FactRow = ({ label, value }) => (
  <div className="sp-fact-row">
    <span className="sp-fact-label">{label}</span>
    <span className="sp-fact-value">{value}</span>
  </div>
);

// ─── Session list item ────────────────────────────────────────────────────────
const SessionItem = ({ session, active, onClick }) => {
  const isBattle    = session.type === 'battle';
  const { dateStr, timeStr } = formatSessionDate(session.date);
  const scoreColor  = getTierColor(session.score);
  const accentColor = isBattle ? (session.won ? '#10B981' : '#EF4444') : scoreColor;

  return (
    <button
      className={`sp-item ${active ? 'active' : ''} ${isBattle ? 'battle' : 'solo'}`}
      style={{ '--sp-item-accent': accentColor }}
      onClick={onClick}
    >
      <div className="sp-item-avatar-wrap">
        {isBattle ? (
          <>
            <Avatar seed={session.opponentUsername} size={32} />
            <span className={`sp-item-badge ${session.won ? 'win' : 'loss'}`}>
              <Swords size={9} />
            </span>
          </>
        ) : (
          <div className="sp-item-icon" style={{ background: `${scoreColor}18`, color: scoreColor }}>
            <Target size={14} />
          </div>
        )}
      </div>

      <div className="sp-item-body">
        <div className="sp-item-title-row">
          <span className="sp-item-title">{session.title}</span>
          {isBattle && session.eloDelta !== undefined && (
            <span className={`sp-elo-chip ${session.eloDelta >= 0 ? 'gain' : 'loss'}`}>
              {session.eloDelta >= 0 ? '+' : ''}{session.eloDelta}
            </span>
          )}
        </div>
        <div className="sp-item-meta">
          {isBattle ? (
            <>
              <span className="sp-item-opp">vs {session.opponentUsername || 'Bot'}</span>
              {session.won !== undefined && (
                <span className={`sp-wl ${session.won ? 'win' : 'loss'}`}>
                  {session.won ? 'W' : 'L'}
                </span>
              )}
            </>
          ) : (
            <span className="sp-item-type solo">
              Solo{session.difficulty ? ` · ${session.difficulty}` : ''}
            </span>
          )}
          <span className="sp-item-date">
            <CalendarDays size={10} />
            {dateStr} · {timeStr}
          </span>
        </div>
      </div>

      {session.score != null && <ScoreChip score={session.score} size="sm" />}
      <ChevronRight size={12} className="sp-item-arrow" />
    </button>
  );
};

// ─── Empty state ──────────────────────────────────────────────────────────────
const EmptyState = ({ onNavigate }) => (
  <div className="sp-empty">
    <div className="sp-empty-icon"><Target size={32} /></div>
    <h3>No sessions yet</h3>
    <p>Complete your first challenge to see it here</p>
    <button className="sp-empty-btn" onClick={onNavigate}>Start a challenge</button>
  </div>
);

// ─── Challenge details block — mirrors BattleArena's ChallengeDetails ─────────
const ChallengeDetails = ({ session }) => {
  const hasObjective   = !!session.objective;
  const hasDescription = !!session.description;
  const hasConstraints = session.constraints?.length > 0;
  const hasRemarks     = !!session.remarks;

  if (!hasObjective && !hasDescription && !hasConstraints && !hasRemarks) {
    return (
      <div className="sp-info-card">
        <div className="sp-info-card-label"><Target size={12} /> Challenge</div>
        <h4 className="sp-challenge-name">{session.title || 'Battle'}</h4>
        <p className="sp-info-card-body" style={{ color: 'var(--sp-text-3)', fontStyle: 'italic' }}>
          Full challenge details weren't recorded for this match.
        </p>
      </div>
    );
  }

  return (
    <div className="sp-challenge-block">

      {/* Title + difficulty */}
      <div className="sp-challenge-title-row">
        <Bot size={18} className="sp-challenge-title-icon" />
        <h2 className="sp-challenge-main-title">{session.title || 'Battle'}</h2>
        {session.difficulty && (
          <span className={`sp-difficulty ${session.difficulty?.toLowerCase()}`}>
            {session.difficulty}
          </span>
        )}
      </div>

      {hasObjective && (
        <div className="sp-challenge-section">
          <h5>🎯 Objective:</h5>
          <p>{session.objective}</p>
        </div>
      )}

      {hasDescription && (
        <div className="sp-challenge-section">
          <h5>📝 Task Description:</h5>
          <p>{session.description}</p>
        </div>
      )}

      {hasConstraints && (
        <div className="sp-challenge-section">
          <h5>⚡ Rules to Follow (Follow all {session.constraints.length} to score high):</h5>
          <div className="sp-constraints-list">
            {session.constraints.map((c, i) => (
              <div key={i} className="sp-constraint-item">
                <span className="sp-constraint-number">{i + 1}.</span>
                <span className="sp-constraint-text">{c}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {hasRemarks && (
        <div className="sp-challenge-section">
          <h5>💡 Additional Notes:</h5>
          <p>{session.remarks}</p>
        </div>
      )}

    </div>
  );
};

// ─── Solo detail ──────────────────────────────────────────────────────────────
const SoloDetail = ({ session }) => {
  const { dateStr, timeStr } = formatSessionDate(session.date);
  const passed = (session.score ?? 0) >= 75;

  return (
    <div className="sp-detail">
      <div className="sp-two-panels">

        {/* LEFT — Your result */}
        <div className="sp-panel sp-panel-left">
          <div className="sp-panel-header">
            <Target size={16} className="sp-panel-icon" />
            <h3>Your Result</h3>
          </div>
          <div className="sp-panel-body">

            <div className={`sp-result-banner ${passed ? 'win' : 'lose'}`}>
              {passed ? <><Trophy size={18} /> Challenge Passed!</> : <>Keep Practising</>}
            </div>

            <div className={`sp-score-card ${passed ? 'winner' : 'loser'}`}>
              {passed && (
                <div className="sp-score-card-crown">
                  <Trophy size={12} /> Passed
                </div>
              )}
              <div className="sp-score-card-avatar-wrap">
                <Avatar seed={session.title} size={52} />
              </div>
              <div className="sp-score-card-name">{session.title}</div>
              <div className={`sp-score-card-score ${passed ? 'pass' : 'fail'}`}>
                {session.score ?? '—'}
                <span className="sp-score-card-max">/100</span>
              </div>
              <div className={`sp-score-card-status ${passed ? 'pass' : 'fail'}`}>
                {passed ? '✓ Passed' : '✗ Below threshold'}
              </div>
            </div>

            {session.breakdown && typeof session.breakdown === 'object' && (
              <div className="sp-info-card">
                <div className="sp-info-card-label"><BarChart2 size={12} /> Score Breakdown</div>
                {Object.entries(session.breakdown).map(([k, v]) =>
                  typeof v === 'number' ? <StatBar key={k} label={k} value={v} /> : null
                )}
              </div>
            )}

            <div className="sp-info-card">
              <div className="sp-info-card-label"><BarChart2 size={12} /> Quick Facts</div>
              <FactRow label="Attempts"   value={session.attempts || 1} />
              <FactRow label="Best Score" value={session.bestScore ?? session.score ?? '—'} />
              <FactRow label="Status"     value={session.status === 'solved' ? '✓ Solved' : 'Pending'} />
              <FactRow label="Completed"  value={`${dateStr} · ${timeStr}`} />
              {session.difficulty && <FactRow label="Difficulty" value={session.difficulty} />}
              {session.topic      && <FactRow label="Topic"      value={session.topic} />}
            </div>

            <div className="sp-prompt-block">
              <div className="sp-prompt-block-header">
                <Zap size={12} /> Your Prompt
              </div>
              <pre className="sp-prompt-pre">{session.finalPrompt || session.prompt || '(not recorded)'}</pre>
            </div>

          </div>
        </div>

        {/* RIGHT — Challenge details */}
        <div className="sp-panel sp-panel-right">
          <div className="sp-panel-header">
            <Zap size={16} className="sp-panel-icon" />
            <h3>Challenge Details</h3>
          </div>
          <div className="sp-panel-body">

            {session.description && (
              <div className="sp-info-card">
                <div className="sp-info-card-label"><Target size={12} /> Description</div>
                <p className="sp-info-card-body">{session.description}</p>
              </div>
            )}

            {session.constraints?.length > 0 && (
              <div className="sp-info-card">
                <div className="sp-info-card-label"><Zap size={12} /> Rules</div>
                <div className="sp-rules-list">
                  {session.constraints.map((c, i) => (
                    <div key={i} className="sp-rule-row">
                      <span className="sp-rule-num">{i + 1}</span>
                      <span className="sp-rule-text">{c}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {session.feedback && (
              <div className="sp-info-card sp-info-card--blue">
                <div className="sp-info-card-label">🤖 AI Feedback</div>
                <p className="sp-info-card-body">{session.feedback}</p>
              </div>
            )}

            {session.aiResponse && (
              <div className="sp-info-card sp-info-card--green">
                <div className="sp-info-card-label">💡 Ideal Prompt</div>
                <p className="sp-info-card-body">{session.aiResponse}</p>
              </div>
            )}

          </div>
        </div>

      </div>
    </div>
  );
};

// ─── Battle detail ────────────────────────────────────────────────────────────
const BattleDetail = ({ session, myUsername }) => {
  const iWon     = session.won;
  const myScore  = session.score ?? 0;
  const oppScore = session.opponentScore ?? 0;
  const pass     = session.minimumScore ?? 75;
  const { dateStr, timeStr } = formatSessionDate(session.date);
  const myPassed  = myScore >= pass;
  const oppPassed = oppScore >= pass;

  return (
    <div className="sp-detail">
      <div className="sp-two-panels">

        {/* LEFT — Your result */}
        <div className="sp-panel sp-panel-left">
          <div className="sp-panel-header">
            <Trophy size={16} className="sp-panel-icon" />
            <h3>Your Result</h3>
          </div>
          <div className="sp-panel-body">

            <div className={`sp-result-banner ${iWon ? 'win' : 'lose'}`}>
              {iWon ? <><Trophy size={18} /> You Won!</> : <>Better luck next time</>}
            </div>

            {session.reason === 'opponent_disconnected' && (
              <p className="sp-result-note">Opponent disconnected — you win by default.</p>
            )}

            {/* My score card */}
            <div className={`sp-score-card ${iWon ? 'winner' : 'loser'}`}>
              {iWon && (
                <div className="sp-score-card-crown">
                  <Crown size={12} /> Winner
                </div>
              )}
              <div className="sp-score-card-avatar-wrap" style={{ position: 'relative' }}>
                <Avatar seed={myUsername} size={52} />
                {iWon && <span className="sp-avatar-ring" />}
              </div>
              <div className="sp-score-card-name">
                {myUsername} <span className="sp-you-badge">YOU</span>
              </div>
              <div className={`sp-score-card-score ${myPassed ? 'pass' : 'fail'}`}>
                {myScore}<span className="sp-score-card-max">/100</span>
              </div>
              <div className={`sp-score-card-status ${myPassed ? 'pass' : 'fail'}`}>
                {myPassed ? '✓ Passed' : '✗ Below threshold'}
              </div>

              {session.eloDelta !== undefined && (
                <div className={`sp-elo-delta ${session.eloDelta >= 0 ? 'gain' : 'loss'}`}>
                  {session.eloDelta >= 0 ? <TrendingUp size={12} /> : <TrendingDown size={12} />}
                  <span className="sp-elo-delta-label">ELO</span>
                  <span className="sp-elo-delta-old">{session.eloOld}</span>
                  <span className="sp-elo-delta-arrow">→</span>
                  <span className="sp-elo-delta-new">{session.eloNew}</span>
                  <span className={`sp-elo-delta-badge ${session.eloDelta >= 0 ? 'gain' : 'loss'}`}>
                    {session.eloDelta >= 0 ? '+' : ''}{session.eloDelta}
                  </span>
                </div>
              )}
            </div>

            {/* Match info */}
            <div className="sp-info-card">
              <div className="sp-info-card-label"><CalendarDays size={12} /> Match Info</div>
              <FactRow label="Mode"       value="Battle" />
              <FactRow label="Played"     value={`${dateStr} · ${timeStr}`} />
              <FactRow label="Pass score" value={`${pass}/100`} />
              {session.difficulty && <FactRow label="Difficulty" value={session.difficulty} />}
              {session.topic      && <FactRow label="Topic"      value={session.topic} />}
            </div>

            {/* My prompt */}
            <div className="sp-prompt-block">
              <div className="sp-prompt-block-header">
                <Avatar seed={myUsername} size={14} /> {myUsername}
              </div>
              <pre className="sp-prompt-pre">{session.myPrompt || '(not submitted)'}</pre>
            </div>

          </div>
        </div>

        {/* RIGHT — Opponent + full challenge details */}
        <div className="sp-panel sp-panel-right">
          <div className="sp-panel-header">
            <Swords size={16} className="sp-panel-icon" />
            <h3>{session.opponentUsername || 'Opponent'}'s Result</h3>
          </div>
          <div className="sp-panel-body">

            <div className={`sp-result-banner ${!iWon ? 'win' : 'lose'}`}>
              {!iWon
                ? <><Trophy size={18} /> {session.opponentUsername} Won!</>
                : <>Better luck next time</>}
            </div>

            {/* Opponent score card */}
            <div className={`sp-score-card ${!iWon ? 'winner' : 'loser'}`}>
              {!iWon && (
                <div className="sp-score-card-crown">
                  <Crown size={12} /> Winner
                </div>
              )}
              <div className="sp-score-card-avatar-wrap" style={{ position: 'relative' }}>
                <Avatar seed={session.opponentUsername} size={52} />
                {!iWon && <span className="sp-avatar-ring" />}
              </div>
              <div className="sp-score-card-name">{session.opponentUsername || 'Unknown'}</div>
              <div className={`sp-score-card-score ${oppPassed ? 'pass' : 'fail'}`}>
                {oppScore}<span className="sp-score-card-max">/100</span>
              </div>
              <div className={`sp-score-card-status ${oppPassed ? 'pass' : 'fail'}`}>
                {oppPassed ? '✓ Passed' : '✗ Below threshold'}
              </div>
            </div>

            {/* Full challenge details block */}
            <ChallengeDetails session={session} />

            {/* Opponent prompt */}
            <div className="sp-prompt-block sp-prompt-block--opp">
              <div className="sp-prompt-block-header">
                <Avatar seed={session.opponentUsername} size={14} /> {session.opponentUsername || 'Opponent'}
              </div>
              <pre className="sp-prompt-pre">{session.opponentPrompt || '(not submitted)'}</pre>
            </div>

          </div>
        </div>

      </div>
    </div>
  );
};

// ─── MAIN PAGE ────────────────────────────────────────────────────────────────
const SessionsPage = () => {
  const navigate = useNavigate();

  const [sidebarOpen, setSidebarOpen]       = useState(false);
  const [selectedSession, setSelected]      = useState(null);
  const [searchTerm, setSearchTerm]         = useState('');
  const [typeFilter, setTypeFilter]         = useState('all');
  const [battleHistory, setBattleHistory]   = useState([]);
  const [loadingBattles, setLoadingBattles] = useState(true);

  const autoSelected = useRef(false);
  const { completedChallenges, loading: soloLoading } = useChallengeStorage();

  const user       = auth.currentUser;
  const myUid      = user?.uid;
  const myUsername = user?.displayName || user?.email?.split('@')[0] || 'You';

  useEffect(() => {
    if (!myUid) { setLoadingBattles(false); return; }
    const load = async () => {
      try {
        const q = query(
          collection(firestore, 'battleHistory'),
          orderBy('playedAt', 'desc'),
          limit(50)
        );
        const snap = await getDocs(q);
        setBattleHistory(
          snap.docs
            .map(d => ({ id: d.id, ...d.data() }))
            .filter(r => r.playerIds?.includes(myUid))
        );
      } catch (e) {
        console.error('Battle history load failed:', e);
      } finally {
        setLoadingBattles(false);
      }
    };
    load();
  }, [myUid]);

  const sessions = useMemo(() => {
    const solo = completedChallenges.map(c => ({
      id: `solo-${c.id || c.title}`, type: 'solo',
      title: c.title, date: c.completedAt, score: c.score,
      language: c.language, difficulty: c.difficulty, topic: c.topic,
      description: c.description, constraints: c.constraints,
      finalPrompt: c.finalPrompt, aiResponse: c.aiResponse,
      feedback: c.feedback, breakdown: c.breakdown, status: c.status,
      attempts: c.attempts, bestScore: c.bestScore,
    }));

    const battles = battleHistory.map(b => {
      const me  = b.players?.find(p => p.userId === myUid);
      const opp = b.players?.find(p => p.userId !== myUid);
      return {
        id: `battle-${b.id}`, type: 'battle',
        title:              b.challengeTitle       || 'Battle',
        date:               b.playedAt,
        score:              me?.score,
        opponentUsername:   opp?.username          || 'Unknown',
        opponentScore:      opp?.score,
        opponentPrompt:     opp?.prompt,
        myPrompt:           me?.prompt,
        won:                b.winnerId === myUid,
        reason:             b.reason,
        // ── Full challenge fields ──
        description:        b.challengeDescription || '',
        objective:          b.challengeObjective   || '',
        remarks:            b.challengeRemarks     || '',
        constraints:        b.constraints          || [],
        difficulty:         b.difficulty,
        topic:              b.topic,
        language:           b.language,
        minimumScore:       b.minimumScore,
        // ── ELO ──
        eloDelta:           b.eloChanges?.[myUid]?.delta,
        eloOld:             b.eloChanges?.[myUid]?.oldElo,
        eloNew:             b.eloChanges?.[myUid]?.newElo,
      };
    });

    return [...solo, ...battles].sort((a, b) => {
      const da = a.date?.seconds ? a.date.seconds : new Date(a.date || 0).getTime() / 1000;
      const db = b.date?.seconds ? b.date.seconds : new Date(b.date || 0).getTime() / 1000;
      return db - da;
    });
  }, [completedChallenges, battleHistory, myUid]);

  useEffect(() => {
    if (!autoSelected.current && sessions.length > 0) {
      setSelected(sessions[0]);
      autoSelected.current = true;
    }
  }, [sessions]);

  const filtered = useMemo(() => sessions.filter(s => {
    const matchType   = typeFilter === 'all' || s.type === typeFilter;
    const q           = searchTerm.toLowerCase();
    const matchSearch = !q
      || s.title.toLowerCase().includes(q)
      || (s.opponentUsername || '').toLowerCase().includes(q);
    return matchType && matchSearch;
  }), [sessions, typeFilter, searchTerm]);

  const loading = soloLoading || loadingBattles;

  const stats = useMemo(() => {
    const soloSessions   = sessions.filter(s => s.type === 'solo');
    const battleSessions = sessions.filter(s => s.type === 'battle');
    const scores         = soloSessions.map(s => s.score).filter(Boolean);
    const avg            = scores.length ? Math.round(scores.reduce((a, b) => a + b, 0) / scores.length) : 0;
    const wins           = battleSessions.filter(s => s.won).length;
    return { total: sessions.length, avg, wins, battles: battleSessions.length };
  }, [sessions]);

  return (
    <div className="sp-shell">
      <Sidebar sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} />
      {sidebarOpen && <div className="sp-overlay" onClick={() => setSidebarOpen(false)} />}

      <div className={`sp-main ${sidebarOpen ? 'sidebar-open' : ''}`}>

        <button className="sp-hamburger" onClick={() => setSidebarOpen(v => !v)}>
          <span /><span /><span />
        </button>

        {/* ── Header ── */}
        <div className="sp-header">
          <div className="sp-header-left">
            <div className="sp-header-icon"><Clock size={18} /></div>
            <div>
              <h1>Sessions</h1>
              <p className="sp-header-sub">{sessions.length} total · {stats.avg > 0 ? `avg score ${stats.avg}` : 'no scores yet'}</p>
            </div>
          </div>
          <div className="sp-header-stats">
            <div className="sp-hstat">
              <span className="sp-hstat-val">{stats.total}</span>
              <span className="sp-hstat-label">Total</span>
            </div>
            <div className="sp-hstat">
              <span className="sp-hstat-val">{stats.avg || '—'}</span>
              <span className="sp-hstat-label">Avg Score</span>
            </div>
            <div className="sp-hstat">
              <span className="sp-hstat-val">{stats.wins}/{stats.battles}</span>
              <span className="sp-hstat-label">Battle W/L</span>
            </div>
          </div>
        </div>

        <div className="sp-body">

          {/* ── Left panel: session list ── */}
          <div className="sp-list-panel">
            <div className="sp-controls">
              <div className="sp-search">
                <Search size={13} />
                <input
                  placeholder="Search sessions…"
                  value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)}
                />
              </div>
              <div className="sp-filters">
                {[
                  { key: 'all',    label: 'All' },
                  { key: 'solo',   label: '🎯 Solo' },
                  { key: 'battle', label: '⚔️ Battle' },
                ].map(f => (
                  <button
                    key={f.key}
                    className={`sp-filter ${typeFilter === f.key ? 'active' : ''}`}
                    onClick={() => setTypeFilter(f.key)}
                  >
                    {f.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="sp-list">
              {loading ? (
                <div className="sp-list-empty">
                  <Loader2 size={20} className="sp-spin" />
                  <span>Loading…</span>
                </div>
              ) : filtered.length === 0 ? (
                <div className="sp-list-empty">
                  <Target size={26} />
                  <span>{searchTerm ? 'No matches' : 'No sessions yet'}</span>
                  {!searchTerm && (
                    <button className="sp-empty-btn sm" onClick={() => navigate('/challenges')}>
                      Start now
                    </button>
                  )}
                </div>
              ) : (
                filtered.map(s => (
                  <SessionItem
                    key={s.id}
                    session={s}
                    active={selectedSession?.id === s.id}
                    onClick={() => setSelected(s)}
                  />
                ))
              )}
            </div>
          </div>

          {/* ── Right panel: detail ── */}
          <div className="sp-detail-panel">
            {!selectedSession ? (
              <EmptyState onNavigate={() => navigate('/challenges')} />
            ) : selectedSession.type === 'battle' ? (
              <BattleDetail session={selectedSession} myUsername={myUsername} />
            ) : (
              <SoloDetail session={selectedSession} />
            )}
          </div>

        </div>
      </div>
    </div>
  );
};

export default SessionsPage;